import { useMemo } from 'react';
import {
  type CreatePatientInput,
  type LabTest,
  type Panel,
  type PatientDetail,
  createPatientSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE, patientJustRegistered } from '#client/lib/routes';
import { initialPatientValues, previewPatientPricing } from './patient-form.logic';
import { PatientBillingFields } from './PatientBillingFields';
import {
  LabTestsCard,
  PanelsCard,
  PatientIdentityCard,
  PatientNoteCard,
} from './PatientFormSections';
import { prefillFrom, usePatientLookup } from './usePatientLookup';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.patients;

interface PatientFormProps {
  /** Present when editing. */
  id?: string | undefined;
  record?: PatientDetail | undefined;
}

export function PatientForm({ id, record }: PatientFormProps) {
  const labTests = useAllRecords<LabTest>({ path: RESOURCE.labTests });
  const panels = useAllRecords<Panel>({ path: RESOURCE.panels });

  const { values, set, patch, setValues } = useFormValues(initialPatientValues(record));

  // Registering only: a visit being edited already belongs to someone.
  const returning = usePatientLookup(values.phone_number ?? '', id === undefined, (match) =>
    setValues((current) => prefillFrom(current, match.last_visit)),
  );

  const priceById = useMemo(
    () => new Map(labTests.items.map((test) => [test.id, test.price])),
    [labTests.items],
  );

  // Derived on every render rather than stored in state, so the summary can
  // never drift from the selection. It is also *only* a summary now: the server
  // runs this same function over `LabTest.price` and stores what it computes,
  // so these figures are a preview of the bill rather than the bill itself.
  // Sending them was how a crafted request could book any total.
  const alreadyPaid = record?.paid ?? 0;
  const pricing = useMemo(
    () => previewPatientPricing(values, alreadyPaid, priceById),
    [values, alreadyPaid, priceById],
  );

  const form = useResourceForm<CreatePatientInput, PatientDetail>({
    path: PATH,
    id,
    schema: createPatientSchema,
    noun: 'Patient',
    createMessage: 'Patient registered',
    // Land on the patient just registered rather than back on the list. The
    // next thing the front desk does is print the slip — so the destination now
    // carries `?registered=1` and opens the print dialog on arrival, rather than
    // leaving the operator to find the button. Sending them to a list would mean
    // searching for someone they created four seconds ago.
    redirectOnCreate: (created) => patientJustRegistered(created.id),
  });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-5">
            <PatientIdentityCard
              values={values}
              onFieldChange={set}
              onFieldsChange={patch}
              errors={form.errors}
              returning={returning}
            />

            <PatientBillingFields
              values={values}
              onFieldsChange={patch}
              errors={form.errors}
              pricing={pricing}
              alreadyPaid={alreadyPaid}
            />

            <PatientNoteCard values={values} onFieldChange={set} errors={form.errors} />
          </div>

          <div className="flex flex-col gap-5">
            <LabTestsCard
              items={labTests.items}
              loading={labTests.loading}
              loadError={labTests.error}
              selectedIds={values.test_ids}
              onChange={(ids) => set('test_ids', ids)}
              error={form.errors.test_ids}
            />
            <PanelsCard
              items={panels.items}
              loading={panels.loading}
              loadError={panels.error}
              selectedIds={values.panel_ids}
              onChange={(ids) => set('panel_ids', ids)}
              error={form.errors.panel_ids}
            />
          </div>
        </div>

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Register patient'}
          submitting={form.submitting}
          submitSize="lg"
        />
      </div>
    </form>
  );
}
